import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import colors from '../styles/colors';

const ContactContainer = styled.section`
  padding: 5rem 0;
  background: ${colors.background.light};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    color: ${colors.text.primary};
    margin-bottom: 1rem;
    position: relative;
    display: inline-block;

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 4px;
      background: ${colors.primary.green};
      border-radius: 2px;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  display: contents;
`;

const InfoItem = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 1.5rem;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid ${colors.primary.green};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
    border-left-color: ${colors.primary.blue};
  }
`;

const Icon = styled.div`
  width: 60px;
  height: 60px;
  background: ${colors.primary.green};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  ${InfoItem}:hover & {
    background: ${colors.primary.blue};
    transform: scale(1.05);
  }
`;

const InfoText = styled.div`
  h4 {
    font-size: 1.2rem;
    font-weight: 700;
    color: ${colors.text.primary};
    margin: 0 0 0.8rem 0;
  }

  p {
    font-size: 1rem;
    color: ${colors.text.secondary};
    margin: 0;
    line-height: 1.4;
  }
`;

const MapContainer = styled(motion.div)`
  width: 100vw;
  height: 300px;
  background: ${colors.neutral.lightGray};
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border: 1px solid rgba(0,150,57,0.2);
  position: relative;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${colors.primary.green}, ${colors.primary.yellow}, ${colors.primary.blue});
    z-index: 1;
  }
`;

const MapOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 15px;

  &:hover {
    opacity: 1;
  }
`;

const MapButton = styled.a`
  background: ${colors.primary.green};
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.primary.blue};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    color: white;
  }
`;

const HoursContainer = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid ${colors.primary.green};
  margin-top: 1rem;
  text-align: center;

  h3 {
    font-size: 1.3rem;
    font-weight: 700;
    color: ${colors.text.primary};
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    color: ${colors.text.secondary};
    margin: 0;
    font-weight: 500;
  }
`;

const SocialButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  background: ${props => props.$whatsapp ? '#25D366' : '#E4405F'};
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  margin: 0.5rem 0.5rem 0.5rem 0;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    color: white;
  }
`;

const Contact = () => {
  const { t } = useTranslation();

  const contactInfo = [
    {
      icon: '📍',
      title: 'Indirizzo',
      text: t('contact.address')
    },
    {
      icon: '📞',
      title: 'Telefono',
      text: t('contact.landline')
    },
    {
      icon: '✉️',
      title: 'Email',
      text: t('contact.email')
    }
  ];

  return (
    <ContactContainer id="contact">
      <Container>
        <Header>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {t('contact.title')}
          </motion.h2>
        </Header>

        <Content>
          <ContactGrid>
            {contactInfo.map((item, index) => (
              <InfoItem
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.6, 
                  delay: index * 0.1
                }}
                viewport={{ once: true }}
              >
                <Icon>{item.icon}</Icon>
                <InfoText>
                  <h4>{item.title}</h4>
                  <p>{item.text}</p>
                </InfoText>
              </InfoItem>
            ))}
          </ContactGrid>

          <HoursContainer
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3>{t('contact.hours')}</h3>
            <p>{t('contact.hoursText')}</p>
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `2px solid ${colors.primary.green}20` }}>
              <h4 style={{ 
                fontSize: '1.1rem', 
                fontWeight: '600', 
                color: colors.primary.blue, 
                marginBottom: '0.5rem' 
              }}>
                {t('contact.aperitivoHours')}
              </h4>
              <p style={{ margin: 0 }}>{t('contact.aperitivoHoursText')}</p>
            </div>
          </HoursContainer>

          <MapContainer
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d837.3327629770157!2d7.691849708519202!3d45.09713831906785!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47886ded2e1d5273%3A0x5d75ee33d0a62a01!2sCorso%20Vercelli%2C%20136%2C%2010155%20Torino%20TO%2C%20It%C3%A1lia!5e0!3m2!1spt-BR!2sbr!4v1757702836744!5m2!1spt-BR!2sbr" 
              width="100%" 
              height="100%" 
              style={{border:0}} 
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização do Cuore Brasiliano"
            />
            <MapOverlay>
              <MapButton 
                href="https://maps.app.goo.gl/j5YSX3eAPzxcyTPC8" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                {t('contact.openInMaps')}
              </MapButton>
            </MapOverlay>
          </MapContainer>
        </Content>
      </Container>
    </ContactContainer>
  );
};

export default Contact;
